/**
 * Itemized purchases: shared vocabulary, a rules-based receipt reader (used when no AI
 * model is available and as a safety net), and the calendar maths for spending insights.
 */
import { CURRENCIES, type Currency } from "./money";

export const ITEM_CATEGORIES = [
  "produce",
  "dairy_eggs",
  "meat_fish",
  "bakery",
  "pantry",
  "frozen",
  "beverages",
  "alcohol",
  "snacks",
  "household",
  "personal_care",
  "baby",
  "pets",
  "health",
  "clothing",
  "electronics",
  "home",
  "fuel",
  "restaurant",
  "services",
  "other",
] as const;
export type ItemCategory = (typeof ITEM_CATEGORIES)[number];

/** Keywords (English and French) → category. First match wins; checked on the normalized name. */
const CATEGORY_WORDS: Array<[ItemCategory, string[]]> = [
  ["alcohol", ["beer", "biere", "wine", "vin ", "vodka", "whisky", "rum", "rhum", "cider", "cidre", "liqueur"]],
  ["dairy_eggs", ["milk", "lait", "cheese", "fromage", "yogurt", "yogourt", "yaourt", "butter", "beurre", "cream", "creme", "egg", "oeuf"]],
  ["meat_fish", ["chicken", "poulet", "beef", "boeuf", "pork", "porc", "lamb", "agneau", "goat", "chevre", "fish", "poisson", "salmon", "saumon", "tilapia", "shrimp", "crevette", "sausage", "saucisse", "turkey", "dinde", "ham", "jambon", "meat", "viande"]],
  ["produce", ["banana", "banane", "plantain", "apple", "pomme", "orange", "lemon", "citron", "tomato", "tomate", "onion", "oignon", "potato", "patate", "pomme de terre", "carrot", "carotte", "lettuce", "laitue", "pepper", "piment", "poivron", "cassava", "manioc", "yam", "igname", "avocado", "avocat", "mango", "mangue", "grape", "raisin", "berry", "fraise", "spinach", "epinard", "garlic", "ail", "ginger", "gingembre", "cucumber", "concombre", "okra", "gombo", "fruit", "legume", "vegetable"]],
  ["bakery", ["bread", "pain", "baguette", "bagel", "croissant", "muffin", "cake", "gateau", "bun", "brioche", "tortilla"]],
  ["frozen", ["frozen", "surgele", "ice cream", "creme glacee", "pizza"]],
  ["beverages", ["water", "eau", "juice", "jus", "coffee", "cafe", "tea", "the ", "soda", "cola", "pop", "drink", "boisson"]],
  ["snacks", ["chips", "cookie", "biscuit", "chocolate", "chocolat", "candy", "bonbon", "cracker", "popcorn", "nuts", "noix", "arachide"]],
  ["pantry", ["rice", "riz", "pasta", "pates", "flour", "farine", "sugar", "sucre", "salt", "sel", "oil", "huile", "sauce", "spice", "epice", "bean", "haricot", "cereal", "cereale", "oat", "avoine", "maggi", "bouillon", "tomato paste", "conserve", "canned", "garri", "semolina", "semoule", "fufu", "couscous", "honey", "miel", "jam", "confiture"]],
  ["household", ["detergent", "lessive", "soap", "savon", "dish", "vaisselle", "paper towel", "essuie", "toilet paper", "papier toilette", "bleach", "javel", "garbage", "poubelle", "foil", "aluminium", "cleaner", "nettoyant", "sponge", "eponge"]],
  ["personal_care", ["shampoo", "shampooing", "toothpaste", "dentifrice", "deodorant", "lotion", "razor", "rasoir", "cosmetic", "hair", "cheveux", "tampon", "pad"]],
  ["baby", ["diaper", "couche", "formula", "lait maternise", "baby", "bebe", "wipes", "lingette"]],
  ["pets", ["dog food", "cat food", "pet", "chien", "chat", "litter", "litiere"]],
  ["health", ["pharmacy", "pharmacie", "vitamin", "vitamine", "tylenol", "advil", "medicine", "medicament", "bandage", "pansement"]],
  ["fuel", ["gas", "essence", "diesel", "fuel", "carburant", "regular", "ordinaire"]],
  ["clothing", ["shirt", "chemise", "pants", "pantalon", "shoe", "chaussure", "dress", "robe", "sock", "chaussette", "jacket", "manteau"]],
  ["electronics", ["cable", "charger", "chargeur", "battery", "pile", "headphone", "ecouteur", "usb", "phone", "telephone"]],
  ["restaurant", ["meal", "repas", "combo", "burger", "poutine", "shawarma", "sandwich", "tip", "pourboire"]],
];

const strip = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

export function categorize(name: string): ItemCategory {
  // Keywords match at the start of a word ("ail" is garlic, not the end of "taille").
  const n = ` ${strip(name).replace(/[^a-z0-9%]+/g, " ")} `;
  for (const [cat, words] of CATEGORY_WORDS) if (words.some((w) => n.includes(` ${w.trim()}`))) return cat;
  return "other";
}

/**
 * A generic product key so the same thing bought at different stores rolls up together:
 * lowercase, no accents, no sizes, codes or brand-ish noise ("LAIT 2% 4L NATREL" → "lait 2%").
 */
export function normalizeProduct(name: string): string {
  let n = strip(name)
    .replace(/\b\d{5,}\b/g, " ") // SKU / PLU codes
    .replace(/\b\d+([.,]\d+)?\s?(kg|g|lb|lbs|oz|ml|l|cl|un|pk|ct|x)\b/g, " ") // sizes
    .replace(/[*#@()[\]{}"'.,:;_\\/-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const words = n.split(" ").filter((w) => w.length > 1 || /\d/.test(w));
  n = words.slice(0, 4).join(" ");
  return n || strip(name).trim().slice(0, 40) || "item";
}

/* ───────── Rules-based receipt reader ───────── */

export interface ExtractedItem {
  name: string;
  product: string;
  category: ItemCategory;
  quantity: number;
  unit: string | null;
  unitPriceMinor: number | null;
  totalMinor: number;
}

export interface ExtractedReceipt {
  merchant: string | null;
  purchasedAt: string | null;
  currency: Currency | null;
  subtotalMinor: number | null;
  taxMinor: number | null;
  totalMinor: number | null;
  paymentMethod: string | null;
  items: ExtractedItem[];
}

const MONEY = /(-?\d{1,3}(?:[ ,. ]\d{3})*(?:[.,]\d{2})|-?\d+(?:[.,]\d{2}))/;
/** Currencies without minor units (CFA francs) print whole amounts: "4 250", "12.500". */
const WHOLE_MONEY = /(-?\d{1,3}(?:[ .\u00a0]\d{3})+|-?\d+)/;

export function toMinor(text: string, currency: Currency): number | null {
  const digits = CURRENCIES[currency].minor;
  let t = text.replace(/[\s $€£₦₵]|fcfa|cfa|xaf|xof|cad|usd|eur/gi, "");
  const neg = t.startsWith("-");
  t = t.replace(/^-/, "");
  // "1.234,56" or "1,234.56": the last separator followed by two digits is the decimal mark.
  const m = t.match(/^(.*?)([.,](\d{2}))?$/);
  if (!m) return null;
  const whole = (m[1] ?? "").replace(/[.,]/g, "");
  if (!/^\d+$/.test(whole || "0")) return null;
  const cents = m[3] ?? "00";
  const value = digits === 0 ? Number(whole || "0") + (Number(cents) >= 50 ? 1 : 0) : Number(whole || "0") * 100 + Number(cents);
  return neg ? -value : value;
}

/** dd/mm/yyyy everywhere except US-dollar receipts (mm/dd/yyyy); yyyy-mm-dd is unambiguous. */
const datePatterns = (dayFirst: boolean): Array<[RegExp, (m: RegExpMatchArray) => string | null]> => [
  [/\b(20\d{2})[-/.](\d{1,2})[-/.](\d{1,2})\b/, (m) => iso(+m[1]!, +m[2]!, +m[3]!)],
  [
    /\b(\d{1,2})[-/.](\d{1,2})[-/.](20\d{2})\b/,
    (m) => (+m[1]! > 12 || (dayFirst && +m[2]! <= 12) ? iso(+m[3]!, +m[2]!, +m[1]!) : iso(+m[3]!, +m[1]!, +m[2]!)),
  ],
];
function iso(y: number, mo: number, d: number): string | null {
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function detectCurrency(text: string, fallback: Currency): Currency {
  const t = text.toUpperCase();
  if (/FCFA|XAF/.test(t)) return /XOF|UEMOA|ABIDJAN|DAKAR/.test(t) ? "XOF" : "XAF";
  if (/XOF/.test(t)) return "XOF";
  if (/€|\bEUR\b/.test(t)) return "EUR";
  if (/₦|\bNGN\b/.test(t)) return "NGN";
  if (/₵|\bGHS\b/.test(t)) return "GHS";
  if (/\bKES\b|\bKSH\b/.test(t)) return "KES";
  if (/\bUSD\b|US\$/.test(t)) return "USD";
  if (/\bCAD\b|TPS|TVQ|GST|HST|QST|CA\$/.test(t)) return "CAD";
  return fallback;
}

const SKIP = /(sub\s?-?total|sous[- ]total|total|tps|tvq|gst|hst|pst|qst|tva|tax|taxe|change|monnaie|cash|comptant|visa|mastercard|master card|amex|debit|d[ée]bit|interac|credit|cr[ée]dit|balance|solde|payment|paiement|rounding|arrondi|points|savings|[ée]conomies|approved|approuv|auth|carte|card|tip|pourboire|merci|thank)/i;

/**
 * Reads plain-text receipts (e-mail bodies, pasted text): one item per line ending in an
 * amount, optional "2 x 3.49" or "1.25 kg @ 4.99/kg" quantities, then SUBTOTAL/TAX/TOTAL.
 */
export function parseReceiptText(text: string, fallbackCurrency: Currency): ExtractedReceipt {
  const currency = detectCurrency(text, fallbackCurrency);
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.replace(/\s+/g, " ").trim())
    .filter(Boolean);
  const merchant = lines.find((l) => /[a-z]/i.test(l) && !MONEY.test(l) && l.length <= 60) ?? null;
  let purchasedAt: string | null = null;
  for (const l of lines) {
    for (const [re, fn] of datePatterns(currency !== "USD")) {
      const m = l.match(re);
      if (m && !purchasedAt) purchasedAt = fn(m);
    }
  }
  let subtotal: number | null = null;
  let tax = 0;
  let sawTax = false;
  let total: number | null = null;
  let paymentMethod: string | null = null;
  const items: ExtractedItem[] = [];
  for (const raw of lines) {
    const pattern = CURRENCIES[currency].minor === 0 ? WHOLE_MONEY : MONEY;
    const amountMatch = raw.match(new RegExp(`\\s${pattern.source}\\s*(?:FCFA|CFA|F|[A-Z]{0,3})\\s*$`, "i"));
    const lower = raw.toLowerCase();
    if (/visa|mastercard|master card|amex|interac|d[ée]bit|cash|comptant|mobile money|orange money|mtn/i.test(raw) && !paymentMethod) {
      paymentMethod = (raw.match(/visa|mastercard|master card|amex|interac|d[ée]bit|cash|comptant|mobile money|orange money|mtn/i)?.[0] ?? "").toLowerCase();
    }
    if (!amountMatch) continue;
    const amount = toMinor(amountMatch[1]!, currency);
    if (amount === null) continue;
    if (/sub\s?-?total|sous[- ]total/i.test(lower)) {
      subtotal = amount;
      continue;
    }
    if (/\b(tps|tvq|gst|hst|pst|qst|tva|tax|taxe)\b/i.test(lower)) {
      tax += amount;
      sawTax = true;
      continue;
    }
    if (/\btotal\b/i.test(lower)) {
      total = amount;
      continue;
    }
    if (SKIP.test(lower)) continue;
    let name = raw.slice(0, amountMatch.index).trim();
    let quantity = 1;
    let unit: string | null = null;
    let unitPrice: number | null = null;
    const weighed = name.match(/(\d+(?:[.,]\d+)?)\s?(kg|lb|g|l)\s*@\s*\$?\s*(\d+[.,]\d{2})\s*\/?\s*(kg|lb|g|l)?/i);
    const multi = name.match(/(\d+)\s?[x×@]\s?\$?\s*(\d+[.,]\d{2})/i);
    if (weighed) {
      quantity = Number(weighed[1]!.replace(",", "."));
      unit = weighed[2]!.toLowerCase();
      unitPrice = toMinor(weighed[3]!, currency);
      name = name.replace(weighed[0], "").trim();
    } else if (multi) {
      quantity = Number(multi[1]);
      unitPrice = toMinor(multi[2]!, currency);
      unit = "each";
      name = name.replace(multi[0], "").trim();
    }
    name = name.replace(/\s+\d{5,}\s*$/, "").replace(/^\d{5,}\s+/, "").trim();
    if (!name || !/[a-z]/i.test(name) || name.length > 80) continue;
    if (amount <= 0 && !/rabais|discount|coupon|remise/i.test(lower)) continue;
    items.push({
      name,
      product: normalizeProduct(name),
      category: categorize(name),
      quantity,
      unit,
      unitPriceMinor: unitPrice ?? (quantity === 1 ? amount : Math.round(amount / quantity)),
      totalMinor: amount,
    });
  }
  const itemsSum = items.reduce((s, i) => s + i.totalMinor, 0);
  return {
    merchant,
    purchasedAt,
    currency,
    subtotalMinor: subtotal ?? (items.length ? itemsSum : null),
    taxMinor: sawTax ? tax : null,
    totalMinor: total ?? (subtotal !== null ? subtotal + tax : items.length ? itemsSum + tax : null),
    paymentMethod,
    items,
  };
}

/* ───────── Insight periods ───────── */

export const INSIGHT_RANGES = ["week", "month", "year", "decade"] as const;
export type InsightRange = (typeof INSIGHT_RANGES)[number];

const d = (iso: string) => new Date(`${iso}T00:00:00Z`);
const fmt = (dt: Date) => dt.toISOString().slice(0, 10);

/**
 * The period containing `anchor` and the buckets it is drawn with:
 * week → 7 days (Mon–Sun), month → days, year → 12 months, decade → 10 years (2020–2029).
 */
export function periodFor(range: InsightRange, anchor: string): { start: string; end: string; buckets: Array<{ key: string; start: string; end: string }> } {
  const a = d(anchor);
  const y = a.getUTCFullYear();
  const m = a.getUTCMonth();
  if (range === "week") {
    const dow = (a.getUTCDay() + 6) % 7; // Monday = 0
    const start = new Date(Date.UTC(y, m, a.getUTCDate() - dow));
    const buckets = Array.from({ length: 7 }, (_, i) => {
      const day = fmt(new Date(start.getTime() + i * 86_400_000));
      return { key: day, start: day, end: day };
    });
    return { start: buckets[0]!.start, end: buckets[6]!.end, buckets };
  }
  if (range === "month") {
    const days = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    const buckets = Array.from({ length: days }, (_, i) => {
      const day = fmt(new Date(Date.UTC(y, m, i + 1)));
      return { key: day, start: day, end: day };
    });
    return { start: buckets[0]!.start, end: buckets.at(-1)!.end, buckets };
  }
  if (range === "year") {
    const buckets = Array.from({ length: 12 }, (_, i) => ({
      key: `${y}-${String(i + 1).padStart(2, "0")}`,
      start: fmt(new Date(Date.UTC(y, i, 1))),
      end: fmt(new Date(Date.UTC(y, i + 1, 0))),
    }));
    return { start: `${y}-01-01`, end: `${y}-12-31`, buckets };
  }
  const decade = Math.floor(y / 10) * 10;
  const buckets = Array.from({ length: 10 }, (_, i) => ({ key: String(decade + i), start: `${decade + i}-01-01`, end: `${decade + i}-12-31` }));
  return { start: `${decade}-01-01`, end: `${decade + 9}-12-31`, buckets };
}

/** The same range, shifted by n periods (n = −1 is the previous one). */
export function shiftAnchor(range: InsightRange, anchor: string, n: number): string {
  const a = d(anchor);
  if (range === "week") return fmt(new Date(a.getTime() + n * 7 * 86_400_000));
  if (range === "month") return fmt(new Date(Date.UTC(a.getUTCFullYear(), a.getUTCMonth() + n, 1)));
  if (range === "year") return `${a.getUTCFullYear() + n}-01-01`;
  return `${Math.floor(a.getUTCFullYear() / 10) * 10 + 10 * n}-01-01`;
}
